// Страница результатов прохождения упражнения: показывает итоговый балл, детальный разбор ответов и кнопки навигации.

import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Badge, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { getStudentExercise } from '../../api/studentApi';
import AppNavbar from '../../components/common/AppNavbar';
import useAuthStore from '../../store/authStore';
import useGuestStore from '../../store/guestStore';

// Replace %1, %2 … with _____ for display
function formatSentence(text) {
  if (!text) return '';
  return text.replace(/%\d+/g, '_____');
}

function ResultIcon({ ratio }) {
  if (ratio >= 0.9) return <span style={{ fontSize: '3rem' }}>🏆</span>;
  if (ratio >= 0.6) return <span style={{ fontSize: '3rem' }}>👍</span>;
  return <span style={{ fontSize: '3rem' }}>💪</span>;
}

// ── Answer card ───────────────────────────────────────────────────────────────

function AnswerCard({ item, explanation, index }) {
  const [open, setOpen] = useState(false);
  const { is_correct, student_answer, correct_answer, question, hint } = item;
  const hasExplanation = !!explanation;

  return (
    <Card
      className="border-0"
      style={{
        borderRadius: '0.75rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        background: '#fff',
        borderLeft: `4px solid ${is_correct ? '#28a745' : '#dc3545'}`,
      }}
    >
      <Card.Body className="p-3">

        {/* Sentence context */}
        {question && (
          <p
            style={{
              fontSize: '0.875rem',
              color: '#6c757d',
              fontStyle: 'italic',
              marginBottom: '0.5rem',
              lineHeight: 1.5,
            }}
          >
            {index}. {formatSentence(question)}
          </p>
        )}

        {/* Answer row */}
        <div className="d-flex flex-wrap align-items-center gap-2">

          {/* Student answer */}
          <div className="d-flex align-items-center gap-1">
            <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Ваш ответ:</span>
            <Badge
              bg={is_correct ? 'success' : 'danger'}
              style={{ fontSize: '0.8rem', fontWeight: 600 }}
            >
              {is_correct ? '✓' : '✗'} {student_answer || '—'}
            </Badge>
          </div>

          {/* Correct answer + hint — only on error */}
          {!is_correct && (
            <>
              <div className="d-flex align-items-center gap-1">
                <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Правильно:</span>
                <Badge bg="success" style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  {correct_answer}
                </Badge>
              </div>

              {hint && (
                <div className="d-flex align-items-center gap-1">
                  <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Слово:</span>
                  <Badge
                    bg="light"
                    text="dark"
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      letterSpacing: '0.04em',
                      border: '1px solid #dee2e6',
                    }}
                  >
                    {hint}
                  </Badge>
                </div>
              )}
            </>
          )}

          {/* Explanation button */}
          {hasExplanation && (
            <Button
              variant="outline-secondary"
              size="sm"
              style={{
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                padding: '0.15rem 0.6rem',
                marginLeft: 'auto',
              }}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? 'Скрыть' : '? Пояснение'}
            </Button>
          )}
        </div>

        {/* Explanation panel */}
        {hasExplanation && open && (
          <div
            style={{
              marginTop: '0.625rem',
              padding: '0.6rem 0.75rem',
              background: '#f0f4ff',
              borderRadius: '0.5rem',
              fontSize: '0.85rem',
              color: '#1e2a4a',
              lineHeight: 1.6,
            }}
          >
            {explanation}
          </div>
        )}

      </Card.Body>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function StudentExerciseResultPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const result = location.state?.result;
  const stateExerciseId = location.state?.exerciseId;
  const stateVariantId  = location.state?.variantId;
  const retryTypeId     = location.state?.retryTypeId;
  const retryTypeName   = location.state?.retryTypeName ?? '';

  const token = useAuthStore((s) => s.token);
  const isGuest = !token;
  const guestStore = useGuestStore();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const incrementedRef = useRef(false);

  const [allVariants, setAllVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);

  useEffect(() => {
    if (!stateExerciseId || !result) return;
    setLoadingVariants(true);
    getStudentExercise(stateExerciseId)
      .then(({ data }) => setAllVariants(data.exercise_variants ?? []))
      .catch(() => {})
      .finally(() => setLoadingVariants(false));
  }, [stateExerciseId]);

  // Инкрементируем счётчик гостя один раз при показе результата
  useEffect(() => {
    if (!isGuest || !result || incrementedRef.current) return;
    incrementedRef.current = true;
    guestStore.increment();
    if (guestStore.isLimitReached()) setShowLimitModal(true);
  }, [isGuest, result]);

  const nextVariant = (() => {
    if (!stateVariantId || allVariants.length < 2) return null;
    const idx = allVariants.findIndex((v) => v.id === Number(stateVariantId));
    return idx === -1 ? null : (allVariants[idx + 1] ?? null);
  })();

  // No result — user navigated directly
  if (!result) {
    return (
      <>
        <AppNavbar />
        <Container className="py-4" style={{ maxWidth: 640 }}>
          <Alert variant="warning" style={{ borderRadius: '0.75rem' }}>
            Результаты не найдены. Пожалуйста, пройдите упражнение.
          </Alert>
          <Button variant="outline-secondary" onClick={() => navigate('/student/exercises')}
            style={{ borderRadius: '0.5rem' }}>
            ← К упражнениям
          </Button>
        </Container>
      </>
    );
  }

  const { errors_count, total_questions, correct_count, details, motivation_message, explanations } = result;
  const ratio        = total_questions > 0 ? correct_count / total_questions : 0;
  const scorePercent = total_questions > 0 ? Math.round(ratio * 100) : 0;

  const accentColor = ratio >= 0.9 ? '#28a745' : ratio >= 0.6 ? '#ffc107' : '#dc3545';
  const motivationVariant = ratio >= 0.9 ? 'success' : ratio >= 0.6 ? 'warning' : 'danger';

  // Group details by sentence (position = "sentId:blankPos")
  const sentenceGroups = [];
  for (const item of (details ?? [])) {
    const sentId = item.position.split(':')[0];
    const last = sentenceGroups[sentenceGroups.length - 1];
    if (last && last.sentId === sentId) {
      last.items.push(item);
    } else {
      sentenceGroups.push({ sentId, items: [item] });
    }
  }

  return (
    <>
      <AppNavbar />

      {/* Модал лимита для гостей */}
      <Modal show={showLimitModal} onHide={() => setShowLimitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700 }}>Лимит исчерпан</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🎯</div>
          <p className="fw-semibold mb-2">Вы прошли доступные бесплатные упражнения</p>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Зарегистрируйтесь бесплатно, чтобы продолжить тренироваться и сохранять прогресс.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-2">
          <Button variant="primary" onClick={() => navigate('/register')} style={{ fontWeight: 600, borderRadius: '0.5rem' }}>
            Зарегистрироваться бесплатно
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate('/login')} style={{ borderRadius: '0.5rem' }}>
            Войти
          </Button>
        </Modal.Footer>
      </Modal>

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)', paddingBottom: '3rem' }}>
        <Container className="py-4" style={{ maxWidth: 680 }}>

          {/* Баннер для гостей */}
          {isGuest && !showLimitModal && (
            <Alert
              className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2"
              style={{ borderRadius: '0.75rem', background: '#f0f4ff', border: '1px solid #c7d7f0', color: '#1e2a4a' }}
            >
              <span style={{ fontSize: '0.9rem' }}>
                <strong>Прогресс не сохраняется</strong> — зарегистрируйтесь, чтобы отслеживать результаты.
                {guestStore.remaining() > 0 && ` Осталось бесплатных упражнений: ${guestStore.remaining()}.`}
              </span>
              <Button
                size="sm"
                variant="primary"
                onClick={() => navigate('/register')}
                style={{ borderRadius: '0.4rem', fontWeight: 600, flexShrink: 0 }}
              >
                Зарегистрироваться
              </Button>
            </Alert>
          )}

          {/* ── Score card ── */}
          <Card
            className="border-0 text-center mb-4"
            style={{ borderRadius: '1rem', boxShadow: '0 4px 24px rgba(0,0,0,0.09)', background: '#fff', overflow: 'hidden' }}
          >
            <div style={{ height: '0.375rem', background: `linear-gradient(90deg, ${accentColor}, ${accentColor}cc)` }} />
            <Card.Body className="p-4 p-md-5">
              <div className="mb-3"><ResultIcon ratio={ratio} /></div>

              <div className="mb-1">
                <span style={{ fontSize: '3rem', fontWeight: 800, color: '#1e2a4a', letterSpacing: '-0.02em', lineHeight: 1 }}>
                  {scorePercent}%
                </span>
              </div>
              <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
                Правильных ответов: {correct_count} из {total_questions}
              </p>

              <Row className="g-3 mb-4">
                {[
                  { label: 'Правильно', value: correct_count, color: '#28a745' },
                  { label: 'Ошибок',   value: errors_count,  color: '#dc3545' },
                  { label: 'Всего',    value: total_questions, color: '#1e2a4a' },
                ].map(({ label, value, color }) => (
                  <Col xs={4} key={label}>
                    <div style={{ background: '#f4f6fb', borderRadius: '0.75rem', padding: '1rem 0.5rem' }}>
                      <div className="fw-bold" style={{ fontSize: '1.5rem', color, lineHeight: 1 }}>{value}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>{label}</div>
                    </div>
                  </Col>
                ))}
              </Row>

              {motivation_message && (
                <Alert variant={motivationVariant} className="mb-0 text-start"
                  style={{ borderRadius: '0.75rem', fontSize: '0.9rem', lineHeight: 1.6 }}>
                  {motivation_message}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* ── Answer breakdown ── */}
          {sentenceGroups.length > 0 && (
            <div className="mb-4">
              <h6 className="fw-bold mb-3" style={{ color: '#1e2a4a' }}>Разбор ответов</h6>
              <div className="d-flex flex-column gap-2">
                {sentenceGroups.map((group, gIdx) =>
                  group.items.map((item, iIdx) => (
                    <AnswerCard
                      key={item.position}
                      item={item}
                      explanation={explanations?.[item.position] ?? null}
                      index={gIdx + 1}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Next steps ── */}
          <Card className="border-0"
            style={{ borderRadius: '1rem', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', background: '#fff' }}>
            <Card.Body className="p-4">
              <h6 className="fw-bold mb-1" style={{ color: '#1e2a4a' }}>Что дальше?</h6>
              <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                {ratio >= 0.9
                  ? 'Отличный результат! Попробуйте что-то новое.'
                  : ratio >= 0.6
                  ? 'Хороший результат! Повторите или переходите к следующему.'
                  : 'Рекомендуем повторить — практика помогает запомнить лучше.'}
              </p>

              {retryTypeId ? (
                /* ── Режим "Повторить сложные" ── */
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Button
                    onClick={() => navigate('/student/retry-errors', {
                      state: { typeId: retryTypeId, typeName: retryTypeName },
                    })}
                    style={{
                      background: ratio < 0.9
                        ? 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)' : '#fff',
                      border: ratio < 0.9 ? 'none' : '1.5px solid var(--bs-primary)',
                      borderRadius: '0.5rem', fontWeight: 600,
                      color: ratio < 0.9 ? '#fff' : 'var(--bs-primary)', flex: 1,
                    }}
                  >
                    Повторить снова
                  </Button>
                  <Button
                    onClick={() => navigate('/student')}
                    style={{
                      background: ratio >= 0.9
                        ? 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)' : '#fff',
                      border: ratio >= 0.9 ? 'none' : '1.5px solid #6c757d',
                      borderRadius: '0.5rem', fontWeight: 600,
                      color: ratio >= 0.9 ? '#fff' : '#6c757d', flex: 1,
                    }}
                  >
                    На главную
                  </Button>
                </div>
              ) : (
                /* ── Обычный режим ── */
                <>
                  <div className="d-flex flex-column flex-sm-row gap-2">
                    {loadingVariants && (
                      <Button disabled style={{ flex: 1, borderRadius: '0.5rem' }}>
                        <Spinner size="sm" animation="border" />
                      </Button>
                    )}
                    {nextVariant && !loadingVariants && (
                      <Button
                        onClick={() => navigate('/student/exercises/exercise',
                          { state: { exerciseId: stateExerciseId, variantId: nextVariant.id } })}
                        style={{
                          background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
                          border: 'none', borderRadius: '0.5rem', fontWeight: 600, color: '#fff', flex: 1,
                        }}
                      >
                        Следующий вариант →
                      </Button>
                    )}

                    <Button
                      onClick={() => navigate('/student/exercises/exercise',
                        { state: { exerciseId: stateExerciseId, ...(stateVariantId ? { variantId: stateVariantId } : {}) } })}
                      style={{
                        background: (!nextVariant && ratio < 0.6)
                          ? 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)' : '#fff',
                        border: (!nextVariant && ratio < 0.6) ? 'none' : '1.5px solid var(--bs-primary)',
                        borderRadius: '0.5rem', fontWeight: 600,
                        color: (!nextVariant && ratio < 0.6) ? '#fff' : 'var(--bs-primary)', flex: 1,
                      }}
                    >
                      Повторить этот вариант
                    </Button>

                    <Button
                      onClick={() => navigate('/student/exercises')}
                      style={{
                        background: (!nextVariant && ratio >= 0.6)
                          ? 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)' : '#fff',
                        border: (!nextVariant && ratio >= 0.6) ? 'none' : '1.5px solid #6c757d',
                        borderRadius: '0.5rem', fontWeight: 600,
                        color: (!nextVariant && ratio >= 0.6) ? '#fff' : '#6c757d', flex: 1,
                      }}
                    >
                      Выбрать другое
                    </Button>
                  </div>

                  <Button
                    variant="link" onClick={() => navigate('/student')}
                    className="w-100 mt-2"
                    style={{ color: '#6c757d', textDecoration: 'none', fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    Закончить на сегодня
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>

        </Container>
      </div>
    </>
  );
}
